import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { Alert, Button, Checkbox, FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField } from "@mui/material";
import { SelectEntidade } from "@/components/SelectEntidade";
import { Add, Edit, Block, CheckCircle, Sync } from "@mui/icons-material";
import { FilterModal } from "@/components/FilterModal";
import { SeloIntegracao } from "@/components/escala/SeloIntegracao";
import { DataField } from "@/components/DataField";
import { CPFMask } from "@/components/mask/CPFMask";
import { cpfOnzeDigitos, cpfValido, formatCPF } from "@/utils/cpfUtils";
import usePagination from "@/hooks/pagination";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useNotification } from "@/context/NotificationContext";
import { useCrud } from "@/hooks/useCrud";
import { DataTable } from "@/components/DataTable";
import { CrudModal } from "@/components/crud/CrudModal";
import { CrudToolbar } from "@/components/crud/CrudToolbar";
import {
  ImportadorPlanilha,
  type ColunaImportacao,
  type LinhaImportacaoErro,
  type LinhaPlanilha,
  type ResultadoImportacao,
} from "@/components/import/ImportadorPlanilha";
import { useLogger } from "@/hooks/useLogger";
import { useFilterState } from "@/hooks/useFilterState";
import { useOpcoesCadastro } from "@/hooks/useOpcoesCadastro";
import { useGerenteDeEscala } from "@/hooks/useGerenteDeEscala";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";
import { capitalizar } from "@/utils/format";
import {
  importarColaboradores,
  listarTodosColaboradores,
  listCargasHorarias,
  listColaboradores,
  listLojas,
  lojaLabel,
  saveColaborador,
  rotuloLoja,
  vincularCargasHorarias,
  ORIGENS_CADASTRO,
  type CargaHoraria,
  type Colaborador,
  type HorarioIntegracao,
  type Loja,
  type OrigemCadastro,
} from "@/api/escala.service";
import {
  linhasAbaCargas,
  mensagemDoReflexoDeCarga,
  motivoCargaDoColaborador,
  motivoCargaTravada,
  opcoesDeCarga,
  resolverCargaPorDescricao,
  resolverVinculosDeCarga,
  rotuloCarga,
} from "@/components/escala/cargaHoraria";

type Opcoes = { lojas: Loja[]; cargas: CargaHoraria[] };

// Colunas do modelo de planilha de importação de colaboradores — só as
// referências (loja pelo CNPJ, carga horária pela descrição) aparecem por nome pro usuário
// preencher; a resolução nome -> id acontece em
// resolverColaboradoresParaImportar, usando as mesmas listas (opcoes) que
// o formulário de cadastro manual já usa.
const colunasImportacaoColaboradores: ColunaImportacao[] = [
  { chave: "nome", rotulo: "Nome", obrigatoria: true, exemplo: "Maria da Silva" },
  { chave: "cpf", rotulo: "CPF", exemplo: "12345678900" },
  { chave: "matricula", rotulo: "Matrícula", exemplo: "1001" },
  { chave: "sexo", rotulo: "Sexo (M/F)", obrigatoria: true, exemplo: "F" },
  { chave: "data_nascimento", rotulo: "Data de Nascimento (DD/MM/AAAA)", rotulosAlternativos: ["Data de Nascimento (AAAA-MM-DD)"], tipo: "data", exemplo: "20/05/1990" },
  { chave: "data_admissao", rotulo: "Data de Admissão (DD/MM/AAAA)", rotulosAlternativos: ["Data de Admissão (AAAA-MM-DD)"], tipo: "data", obrigatoria: true, exemplo: "10/01/2024" },
  { chave: "loja", rotulo: "Loja (CNPJ)", obrigatoria: true, exemplo: "00000000000191" },
  { chave: "setor", rotulo: "Setor", exemplo: "Padaria" },
  { chave: "cargo", rotulo: "Cargo", exemplo: "Atendente" },
  { chave: "carga_horaria", rotulo: "Carga horária", exemplo: "Comercial", podeFaltarNoCabecalho: true },
  { chave: "entra_em_escala", rotulo: "Entra em Escala (sim/não)", exemplo: "sim" },
  { chave: "ativo", rotulo: "Ativo (sim/não)", exemplo: "sim" },
];

// Planilha "Vincular cargas horárias": dá carga a quem já está cadastrado. O colaborador é achado
// pela matrícula ou pelo CPF; carga em branco remove o vínculo.
const colunasVincularCargas: ColunaImportacao[] = [
  { chave: "matricula", rotulo: "Matrícula", exemplo: "1001" },
  { chave: "cpf", rotulo: "CPF", exemplo: "12345678900" },
  { chave: "carga_horaria", rotulo: "Carga horária", exemplo: "Comercial" },
];

function apenasDigitos(v: string) {
  return v.replace(/\D/g, "");
}

// interpretarBooleano aceita qualquer grafia razoável de "sim" na planilha
// (o restante — "não", vazio, qualquer outra coisa — cai no padrão) em vez
// de exigir um valor exato tipo "true"/"false", que não é natural pra
// alguém preenchendo uma célula do Excel.
function interpretarBooleano(v: string | undefined, padrao: boolean): boolean {
  const t = (v ?? "").trim().toLowerCase();
  if (t === "") return padrao;
  return ["sim", "s", "true", "1", "verdadeiro", "yes"].includes(t);
}

// resolverColaboradoresParaImportar traduz as linhas cruas da planilha
// (strings, com loja/situação por nome) para o shape que
// escala.importar_colaboradores espera (ids resolvidos), usando as mesmas
// listas já carregadas pro formulário de cadastro manual. Linha é 1-based,
// mesma convenção do Method — se alguma linha não resolve, o índice já
// bate com o que apareceria num erro vindo do backend.
function resolverColaboradoresParaImportar(
  linhas: LinhaPlanilha[],
  opcoes: Opcoes,
): { colaboradores: Colaborador[]; erros: LinhaImportacaoErro[] } {
  const erros: LinhaImportacaoErro[] = [];
  const colaboradores: Colaborador[] = [];

  linhas.forEach((linha, i) => {
    const numeroLinha = i + 1;

    const loja = opcoes.lojas.find((l) => apenasDigitos(l.cnpj) === apenasDigitos(linha.loja ?? ""));
    if (!loja) {
      erros.push({ linha: numeroLinha, erro: `loja com CNPJ "${linha.loja}" não encontrada` });
      return;
    }

    const sexo = (linha.sexo ?? "").trim().toUpperCase();
    if (sexo !== "M" && sexo !== "F") {
      erros.push({ linha: numeroLinha, erro: `sexo deve ser M ou F, veio "${linha.sexo ?? ""}"` });
      return;
    }

    // Mesma regra de validarLinhaColaborador (colaborador_importar.go), depois de completar os zeros.
    const cpf = cpfOnzeDigitos(linha.cpf ?? "");
    if (cpf && !cpfValido(cpf)) {
      erros.push({ linha: numeroLinha, erro: `CPF "${(linha.cpf ?? "").trim()}" inválido` });
      return;
    }

    // Pela descrição de uma carga ativa; vazio = sem carga.
    const carga = resolverCargaPorDescricao(linha.carga_horaria, opcoes.cargas);
    if (carga.erro) {
      erros.push({ linha: numeroLinha, erro: carga.erro });
      return;
    }

    colaboradores.push({
      nome: linha.nome ?? "",
      cpf: cpf || undefined,
      matricula: linha.matricula || undefined,
      sexo,
      data_nascimento: linha.data_nascimento || undefined,
      data_admissao: linha.data_admissao ?? "",
      loja_id: loja.id,
      setor: linha.setor?.trim() || undefined,
      cargo: linha.cargo?.trim() || undefined,
      entra_em_escala: interpretarBooleano(linha.entra_em_escala, true),
      ativo: interpretarBooleano(linha.ativo, true),
      carga_horaria_id: carga.id,
    });
  });

  return { colaboradores, erros };
}

interface ColaboradorFormProps {
  values: Partial<Colaborador>;
  setValues: Dispatch<SetStateAction<Partial<Colaborador>>>;
  isEditing: boolean;
  opcoes: Opcoes;
  // Carga gravada no colaborador em edição: continua entre as opções mesmo inativada.
  cargaAtualId?: string | null;
  // Sistema externo que trava os dados vindos dele; vazio = tudo editável.
  travadoPor?: string;
  // Horário que o sistema externo dá ao colaborador: a carga só aceita as equivalentes a ele.
  horario?: HorarioIntegracao | null;
}

function ColaboradorForm({ values, setValues, opcoes, cargaAtualId, travadoPor, horario }: ColaboradorFormProps) {
  const travado = Boolean(travadoPor);
  return (
    <>
      {travado && (
        <Alert severity="info">
          Vem da integração ({travadoPor}): nome, CPF, sexo, datas, loja, setor, cargo e ativo só mudam lá.
        </Alert>
      )}

      <TextField
        label="Nome"
        fullWidth
        required
        disabled={travado}
        value={values.nome ?? ""}
        onChange={(e) => setValues((v) => ({ ...v, nome: e.target.value }))}
        slotProps={{ htmlInput: { maxLength: 200 } }}
      />

      <Stack direction="row" spacing={2}>
        <TextField
          label="CPF"
          placeholder="000.000.000-00"
          fullWidth
          disabled={travado}
          value={formatCPF(values.cpf ?? "")}
          onChange={(e) =>
            setValues((v) => ({ ...v, cpf: e.target.value.replace(/\D/g, "").slice(0, 11) }))
          }
          slotProps={{ input: { inputComponent: CPFMask as any } }}
        />
        <TextField
          label="Matrícula"
          fullWidth
          value={values.matricula ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, matricula: e.target.value }))}
          slotProps={{ htmlInput: { maxLength: 30 } }}
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <DataField
          label="Data de Nascimento"
          fullWidth
          disabled={travado}
          value={values.data_nascimento ?? ""}
          onChange={(data) => setValues((v) => ({ ...v, data_nascimento: data }))}
        />
        <DataField
          label="Data de Admissão"
          fullWidth
          required
          disabled={travado}
          value={values.data_admissao ?? ""}
          onChange={(data) => setValues((v) => ({ ...v, data_admissao: data }))}
        />
      </Stack>

      <SelectEntidade
        label="Loja"
        options={opcoes.lojas}
        value={values.loja_id}
        onChange={(id) => setValues((v) => ({ ...v, loja_id: id ?? undefined }))}
        getLabel={lojaLabel}
        required
        disabled={travado}
      />

      <Stack direction="row" spacing={2}>
        <TextField
          label="Setor"
          fullWidth
          disabled={travado}
          value={values.setor ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, setor: e.target.value }))}
          slotProps={{ htmlInput: { maxLength: 120 } }}
        />
        <TextField
          label="Cargo"
          fullWidth
          disabled={travado}
          value={values.cargo ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, cargo: e.target.value }))}
          slotProps={{ htmlInput: { maxLength: 120 } }}
        />
      </Stack>

      <SelectEntidade
        label="Carga horária"
        options={opcoesDeCarga(opcoes.cargas, cargaAtualId, horario)}
        value={values.carga_horaria_id}
        onChange={(id) => setValues((v) => ({ ...v, carga_horaria_id: id }))}
        getLabel={rotuloCarga}
        required={Boolean(horario)}
        helperText={
          horario
            ? `Vem da integração (${travadoPor}): só aparecem as cargas com o mesmo horário de segunda a sábado.`
            : undefined
        }
      />

      <TextField
        select
        label="Sexo"
        fullWidth
        required
        disabled={travado}
        value={values.sexo ?? ""}
        onChange={(e) => setValues((v) => ({ ...v, sexo: e.target.value as Colaborador["sexo"] }))}
      >
        <MenuItem value="M">Masculino</MenuItem>
        <MenuItem value="F">Feminino</MenuItem>
      </TextField>

      <FormControlLabel
        control={
          <Switch
            checked={values.entra_em_escala ?? true}
            onChange={(e) => setValues((v) => ({ ...v, entra_em_escala: e.target.checked }))}
          />
        }
        label="Entra em escala"
      />

      <FormControlLabel
        control={
          <Switch
            disabled={travado}
            checked={values.ativo ?? true}
            onChange={(e) => setValues((v) => ({ ...v, ativo: e.target.checked }))}
          />
        }
        label="Ativo"
      />
    </>
  );
}

export function EscalaColaboradoresTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { log } = useLogger();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Colaborador>();

  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  // Só vira true na primeira carga e nunca mais volta — recargas seguintes não trocam
  // toolbar/tabela por um spinner de página inteira (R47, mesmo padrão de useListaRemota).
  const [carregouAlgumaVez, setCarregouAlgumaVez] = useState(false);
  const chamada = useRef(0);

  const [toggleAtivo, setToggleAtivo] = useState<Colaborador | null>(null);
  // salvar, importar e vincular cargas exigem Gerente no servidor; abaixo disso a aba é só consulta.
  const { podeVerRelatorios: gerente } = useGerenteDeEscala();

  const { inputValues, setInputValues, filtros, applyFilters, handleClearFilters } = useFilterState(
    { loja_id: "", setor: "", cargo: "", nome: "", origem: "" as OrigemCadastro | "" },
    () => setPage(0),
  );

  // Cada lista resolvida de forma independente (useOpcoesCadastro).
  const { opcoes } = useOpcoesCadastro<Opcoes>(
    {
      lojas: { buscar: () => listLojas(clientId), mensagemErro: "Erro ao carregar lojas" },
      cargas: { buscar: () => listCargasHorarias(clientId), mensagemErro: "Erro ao carregar cargas horárias" },
    },
    [clientId],
  );

  const loadData = async () => {
    const minha = ++chamada.current;
    try {
      const result = await listColaboradores(
        {
          loja_id: filtros.loja_id || undefined,
          setor: filtros.setor || undefined,
          cargo: filtros.cargo || undefined,
          nome: filtros.nome || undefined,
          origem: filtros.origem || undefined,
          page: page + 1,
          page_size: rowsPerPage,
        },
        clientId,
      );
      if (minha !== chamada.current) return;
      setColaboradores(result.items ?? []);
      setTotal(result.total ?? 0);
    } catch (error) {
      if (minha === chamada.current) notify("Erro ao carregar colaboradores", "error");
    } finally {
      if (minha === chamada.current) setCarregouAlgumaVez(true);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, filtros, page, rowsPerPage]);

  const activeFilterCount = Object.values(filtros).filter((v) => v).length;

  const rotuloCargaDoColaborador = (c: Colaborador) => {
    const carga = opcoes.cargas.find((ch) => ch.id === c.carga_horaria_id);
    return carga ? rotuloCarga(carga) : "";
  };

  const onSave = async (values: Partial<Colaborador>) => {
    if (!values.nome?.trim()) {
      notify("Nome é obrigatório", "error");
      return;
    }
    if (!values.data_admissao) {
      notify("Data de admissão é obrigatória", "error");
      return;
    }
    if (!values.loja_id) {
      notify("Loja é obrigatória", "error");
      return;
    }
    // O art. 386 da CLT (domingos da mulher) depende do sexo — sem ele a
    // colaboradora seria tratada como homem sem nenhum aviso.
    if (values.sexo !== "M" && values.sexo !== "F") {
      notify("Sexo é obrigatório", "error");
      return;
    }
    const motivoCarga =
      motivoCargaDoColaborador(values.carga_horaria_id, editing?.carga_horaria_id, opcoes.cargas) ??
      (editing ? motivoCargaTravada(editing, values.carga_horaria_id, opcoes.cargas) : null);
    if (motivoCarga) {
      notify(capitalizar(motivoCarga), "error");
      return;
    }

    try {
      const result = await saveColaborador(
        {
          id: editing?.id,
          matricula: values.matricula || undefined,
          nome: values.nome,
          cpf: values.cpf || undefined,
          sexo: values.sexo,
          data_nascimento: values.data_nascimento || undefined,
          data_admissao: values.data_admissao,
          loja_id: values.loja_id,
          setor: values.setor?.trim() || undefined,
          cargo: values.cargo?.trim() || undefined,
          entra_em_escala: values.entra_em_escala ?? true,
          ativo: values.ativo ?? true,
          carga_horaria_id: values.carga_horaria_id || undefined,
        },
        clientId,
      );

      log(editing ? LogEventType.Updated : LogEventType.Created, result, "escala_colaborador", result.id, "info");
      const aviso = mensagemDoReflexoDeCarga(`Colaborador ${editing ? "atualizado" : "criado"}!`, result);
      notify(aviso.texto, aviso.tipo);
      loadData();
      handleClose();
    } catch (e: any) {
      notify(e?.message || "Erro ao salvar colaborador", "error");
    }
  };

  // handleImportarColaboradores é o onImportar do ImportadorPlanilha:
  // resolve as referências por nome usando `opcoes` (já carregado pra
  // tela); se alguma linha não resolve, devolve os erros sem nem chamar o
  // backend — nada é importado até toda a planilha resolver e o Method
  // validar de novo do seu lado.
  const handleImportarColaboradores = async (linhasPlanilha: LinhaPlanilha[]): Promise<ResultadoImportacao> => {
    const { colaboradores: resolvidos, erros: errosResolucao } = resolverColaboradoresParaImportar(
      linhasPlanilha,
      opcoes,
    );
    if (errosResolucao.length > 0) {
      return { importados: 0, erros: errosResolucao };
    }
    return importarColaboradores(resolvidos, clientId);
  };

  // O colaborador sai da lista inteira (as lojas que o usuário enxerga), lida na hora da importação.
  const handleVincularCargas = async (linhasPlanilha: LinhaPlanilha[]): Promise<ResultadoImportacao> => {
    const todos = await listarTodosColaboradores({}, clientId);
    const { vinculos, erros } = resolverVinculosDeCarga(linhasPlanilha, todos, opcoes.cargas);
    if (erros.length > 0) {
      return { importados: 0, erros };
    }
    // Como no cadastro: diz os dias refeitos e as escalas validadas ou aprovadas que não mudaram.
    const resultado = await vincularCargasHorarias(vinculos, clientId);
    return {
      ...resultado,
      mensagem: mensagemDoReflexoDeCarga(`${resultado.importados} vínculo(s) atualizado(s) com sucesso.`, resultado),
    };
  };

  const onToggleAtivoConfirm = async () => {
    if (!toggleAtivo) return;
    try {
      const ativo = !toggleAtivo.ativo;
      const result = await saveColaborador({ ...toggleAtivo, ativo }, clientId);
      log(ativo ? LogEventType.Activated : LogEventType.Deactivated, result, "escala_colaborador", toggleAtivo.id, "info");

      notify(ativo ? "Colaborador ativado!" : "Colaborador inativado!", "success");
      loadData();
    } catch (e: any) {
      notify(e?.message || "Erro ao atualizar status", "error");
    } finally {
      setToggleAtivo(null);
    }
  };

  if (!carregouAlgumaVez) return <Sync />;

  return (
    <>
      <CrudToolbar>
        <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
          <TextField
            label="Nome"
            size="small"
            fullWidth
            value={inputValues.nome}
            onChange={(e) => setInputValues((v) => ({ ...v, nome: e.target.value }))}
          />
          <SelectEntidade
            label="Loja"
            options={opcoes.lojas}
            value={inputValues.loja_id}
            onChange={(id) => setInputValues((v) => ({ ...v, loja_id: id ?? "" }))}
            getLabel={lojaLabel}
            size="small"
          />
          <TextField
            label="Setor"
            size="small"
            fullWidth
            value={inputValues.setor}
            onChange={(e) => setInputValues((v) => ({ ...v, setor: e.target.value }))}
          />
          <TextField
            label="Cargo"
            size="small"
            fullWidth
            value={inputValues.cargo}
            onChange={(e) => setInputValues((v) => ({ ...v, cargo: e.target.value }))}
          />
          <TextField
            select
            label="Origem"
            size="small"
            fullWidth
            value={inputValues.origem}
            onChange={(e) => setInputValues((v) => ({ ...v, origem: e.target.value as OrigemCadastro | "" }))}
          >
            <MenuItem value="">Todas</MenuItem>
            {ORIGENS_CADASTRO.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </FilterModal>
        {gerente && (
          <Stack direction="row" spacing={2}>
            <ImportadorPlanilha
              titulo="Importar colaboradores"
              rotuloBotao="Importar colaboradores"
              nomeArquivoModelo="modelo_colaboradores.xlsx"
              colunas={colunasImportacaoColaboradores}
              onImportar={handleImportarColaboradores}
              onConcluido={loadData}
              abasDeConsulta={[{ nome: "Cargas horárias", linhas: linhasAbaCargas(opcoes.cargas) }]}
            />
            <ImportadorPlanilha
              titulo="Vincular cargas horárias"
              rotuloBotao="Vincular cargas horárias"
              nomeArquivoModelo="modelo_vincular_cargas.xlsx"
              colunas={colunasVincularCargas}
              onImportar={handleVincularCargas}
              onConcluido={loadData}
              abasDeConsulta={[{ nome: "Cargas horárias", linhas: linhasAbaCargas(opcoes.cargas) }]}
            />
            <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
              Adicionar colaborador
            </Button>
          </Stack>
        )}
      </CrudToolbar>

      <DataTable
        data={colaboradores}
        columns={[
          { key: "ativo", label: "Ativo", render: (c) => <Checkbox checked={c.ativo} disabled /> },
          { key: "matricula", label: "Matrícula" },
          {
            key: "nome",
            label: "Nome",
            render: (c) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{c.nome}</span>
                <SeloIntegracao sistema={c.travado_por} />
              </Stack>
            ),
          },
          { key: "loja_id", label: "Loja", render: (c) => rotuloLoja(opcoes.lojas, c.loja_id) },
          { key: "setor", label: "Setor" },
          { key: "cargo", label: "Cargo" },
          {
            key: "carga_horaria_id",
            label: "Carga horária",
            render: (c) => rotuloCargaDoColaborador(c),
            exportValue: (c) => rotuloCargaDoColaborador(c),
          },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={
          gerente
            ? (colaborador) => (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <IconButton aria-label={`Editar ${colaborador.nome}`} onClick={() => handleEdit(colaborador)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    aria-label={`${colaborador.ativo ? "Inativar" : "Ativar"} ${colaborador.nome}`}
                    onClick={() => setToggleAtivo(colaborador)}
                    disabled={Boolean(colaborador.travado_por)}
                    color={colaborador.ativo ? "warning" : "success"}
                  >
                    {colaborador.ativo ? <Block /> : <CheckCircle />}
                  </IconButton>
                </Stack>
              )
            : undefined
        }
      />

      <CrudModal<
        Colaborador,
        { opcoes: Opcoes; cargaAtualId?: string | null; travadoPor?: string; horario?: HorarioIntegracao | null }
      >
        open={open}
        onClose={handleClose}
        title={editing ? "Editar colaborador" : "Adicionar colaborador"}
        initialData={editing}
        onSubmit={onSave}
        Form={ColaboradorForm}
        extraFormProps={{
          opcoes,
          cargaAtualId: editing?.carga_horaria_id,
          travadoPor: editing?.travado_por,
          horario: editing?.horario_integracao,
        }}
      />

      <ConfirmModal
        open={!!toggleAtivo}
        onClose={() => setToggleAtivo(null)}
        onConfirm={onToggleAtivoConfirm}
        title={toggleAtivo?.ativo ? "Inativação" : "Ativação"}
        btnText="Confirmar"
        text={`Deseja ${toggleAtivo?.ativo ? "inativar" : "ativar"} o colaborador ${toggleAtivo?.nome}?`}
      />
    </>
  );
}
